import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Typography } from 'antd';

import Book from '@/components/icons/Book';
import Box from '@/components/icons/Box';
import Calendar from '@/components/icons/Calendar';
import Course from '@/components/icons/Course';
import Globe from '@/components/icons/Globe';
import { BLINKET_FRONT_URL, MYEDUZZ_FRONT_URL } from '@/env';
import { useContentRenderIdentifier } from '@/hooks/useContentRenderIdentifier';
import { AvailableSearchParams, getSearchParam } from '@/lib/params';
import { PostMessageAction, sendMessage } from '@/lib/postMessage';
import { ebookSteps } from '@/pages/CreateProduct/steps';
import { stepStore } from '@/stores/steps';

import ProductSelector, { type SelectionOption } from './ProductSelector';
import Wizard from '../..';

const buttonMessage = {
  ebook: 'Criar e-book ou entrega de arquivo',
  course: 'Criar curso',
  physical: 'Criar produto físico',
  event: 'Criar evento',
  service: 'Criar serviço'
} as const;

const SelectProduct = () => {
  const [selected, setSelected] = useState<SelectionOption>();
  const isApp = useMemo(() => getSearchParam(AvailableSearchParams.APP) === 'true', []);
  const navigate = useNavigate();

  const defineProduct = useCallback(() => {
    sendMessage(PostMessageAction.PRODUCT_TYPE_SELECTED, {
      type: selected?.itemKey,
      title: selected?.title,
      redirectTo: selected?.redirectTo
    });

    if (isApp && selected?.redirectTo) {
      return;
    }

    if (selected?.redirectTo) {
      window.open(selected.redirectTo, '_self');
      return;
    }

    stepStore.getState().addSteps({ steps: selected?.steps || [], removeStepsOnReturn: true });
    stepStore.getState().nextStep();
  }, [selected, isApp]);

  useContentRenderIdentifier({
    content: 'select_product',
    onGoBackRender: () => stepStore.getState().removeStepsAhead()
  });

  const handleDismiss = useCallback(() => {
    sendMessage(PostMessageAction.PRODUCT_TYPE_DISMISSED, {});

    if (!isApp) {
      navigate(-1);
    }
  }, [isApp, navigate]);

  return (
    <>
      <section className='product-selector flex h-full flex-col overflow-hidden lg:overflow-y-auto'>
        <Typography.Title className='text-primary! mb-4! shrink-0 text-center lg:mb-8!' level={3}>
          O que vamos criar juntos?
        </Typography.Title>
        <ProductSelector
          selectedKey='ebook'
          onSelect={setSelected}
          list={[
            {
              title: 'E-book',
              subtitle: 'PDF, Planilha ou outros formatos',
              icon: Book,
              itemKey: 'ebook',
              steps: ebookSteps
            },
            {
              title: 'Curso com área de membros',
              icon: Course,
              itemKey: 'course',
              redirectTo: `${MYEDUZZ_FRONT_URL}/producer/product/newproduct/digital`
            },
            {
              title: 'Produto físico',
              icon: Box,
              itemKey: 'physical',
              redirectTo: `${MYEDUZZ_FRONT_URL}/producer/product/newproduct/physical`
            },
            {
              title: 'Evento online ou presencial',
              icon: Calendar,
              itemKey: 'event',
              redirectTo: `${BLINKET_FRONT_URL}/event?utm_source=product-manager&utm_medium=newproduct`
            },
            {
              title: 'Serviço online',
              icon: Globe,
              itemKey: 'service',
              redirectTo: `${MYEDUZZ_FRONT_URL}/producer/product/newproduct/service`
            }
          ]}
        />
      </section>

      <Wizard.Footer>
        <Button id='create' type='primary' disabled={selected?.disabled} onClick={defineProduct}>
          {buttonMessage[(selected?.itemKey as keyof typeof buttonMessage) || 'ebook']}
        </Button>

        <Button id='cancel' variant='filled' color='default' type='default' onClick={handleDismiss}>
          Não quero criar nenhum produto agora
        </Button>
      </Wizard.Footer>
    </>
  );
};
export default SelectProduct;
